import { useDocumentBranding } from "./hooks/useDocumentBranding";
import {
  Route,
  Routes,
  Navigate,
  CheckAuth,
  HomeLayout,
  useAppSelector,
  NotFoundPage,
  LoginPage,
  checkAuthStatus,
  useEffect,
  useAppDispatch,
  AnimatePresence,
  AnimatedRoute,
  Toaster,
  wsClient,
  getActorTypeFromHost,
  checkAgentAuthStatus,
  AdminRoutes,
  AgentRoutes,
  AgentLoginPage,
  AgentHomeLayout,
  AgentNotFoundPage,
  ForgotPasswordPage,
  AgentForgotPasswordPage,
  getAllCountries,
  Suspense,
  PageLoader,
} from "./import";
import { refreshAdminSession, refreshAgentSession } from "./config/apiClients";
import { chatSocket } from "./services/chatSocket";

function App() {
  const actorType = getActorTypeFromHost();
  useDocumentBranding(actorType);
  const isSalesAgent = actorType === "salesAgent" ? true : false;
  const adminAuth = useAppSelector((state) => state.auth);
  const agentAuth = useAppSelector((state) => state.agentAuth);
  const isAuthenticated = isSalesAgent
    ? agentAuth.isAuthenticated
    : adminAuth.isAuthenticated;

  const dispatch = useAppDispatch();

  useEffect(() => {
    if (isSalesAgent) {
      dispatch(checkAgentAuthStatus());
    } else {
      dispatch(checkAuthStatus());
    }
  }, [dispatch, isSalesAgent]);

  useEffect(() => {
    if (isAuthenticated && isSalesAgent) {
      chatSocket.connect(import.meta.env.VITE_CHAT_WS_URL, "agent", refreshAgentSession);
    } else if (isAuthenticated) {
      dispatch(getAllCountries());
      wsClient.connect(import.meta.env.VITE_WS_URL, "admin", refreshAdminSession);
      chatSocket.connect(import.meta.env.VITE_CHAT_WS_URL, "admin", refreshAdminSession);
    } else {
      wsClient.disconnect();
      chatSocket.disconnect();
    }
  }, [isAuthenticated, isSalesAgent, dispatch]);

  return (
    <>
      <Toaster position="top-right" toastOptions={{ duration: 3000 }} />
      <Suspense fallback={<PageLoader fullScreen />}>
      <AnimatePresence mode="wait">
        <Routes>
          <Route
            path="/"
            element={
              <Navigate
                to={
                  !isAuthenticated
                    ? "/login"
                    : actorType === "salesAgent"
                      ? "/dashboard"
                      : "/home"
                }
                replace
              />
            }
          />
          {actorType === "admin" ? (
            <Route
              element={
                <CheckAuth isAuthenticated={isAuthenticated}>
                  <AnimatedRoute>
                    <HomeLayout />
                  </AnimatedRoute>
                </CheckAuth>
              }
            >
              {AdminRoutes()}
            </Route>
          ) : (
            <Route
              element={
                <CheckAuth isAuthenticated={isAuthenticated}>
                  <AnimatedRoute>
                    <AgentHomeLayout />
                  </AnimatedRoute>
                </CheckAuth>
              }
            >
              {AgentRoutes()}
            </Route>
          )}

          <Route
            path="login"
            element={
              isAuthenticated ? (
                <Navigate
                  to={isSalesAgent ? "/dashboard" : "/home"}
                  replace
                />
              ) : isSalesAgent ? (
                <AnimatedRoute>
                  <AgentLoginPage />
                </AnimatedRoute>
              ) : (
                <AnimatedRoute>
                  <LoginPage />
                </AnimatedRoute>
              )
            }
          />
          <Route
            path="forgot-password"
            element={
              <AnimatedRoute>
                {isSalesAgent ? (
                  <AgentForgotPasswordPage />
                ) : (
                  <ForgotPasswordPage />
                )}
              </AnimatedRoute>
            }
          />
          <Route
            path="*"
            element={
              <AnimatedRoute>
                {isSalesAgent ? <AgentNotFoundPage /> : <NotFoundPage />}
              </AnimatedRoute>
            }
          />
        </Routes>
      </AnimatePresence>
      </Suspense>
    </>
  );
}

export default App;
